<template>
  <div class="appeal-review-page">
    <!-- 顶部标题栏 -->
    <div class="review-header">
      <div class="header-left">
        <el-button icon="el-icon-back" @click="goBack">返回</el-button>
        <span class="title">申诉审核 - {{ batchName }}</span>
      </div>
      <div class="header-center">
        <span class="stats">
          总计 <strong>{{ totalCount }}</strong> 份 | 
          待审核 <strong>{{ pendingCount }}</strong> | 
          已完成 <strong>{{ completedCount }}</strong>
        </span>
      </div>
      <div class="header-right">
        <!-- 预留右侧区域 -->
      </div>
    </div>

    <!-- 三栏布局 -->
    <div class="review-content">
      <!-- 左侧：店铺列表 -->
      <div class="left-panel">
        <div class="panel-header">
          <span class="panel-title">申诉名单</span>
          <span class="progress">{{ completedCount }}/{{ totalCount }}</span>
        </div>
        
        <div class="search-box">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索店号/店名" 
            prefix-icon="el-icon-search"
            size="small"
            clearable
            @keyup.enter.native="filterRecords"
            @clear="filterRecords"
          ></el-input>
        </div>
        
        <div class="record-list">
          <div
            v-for="(record, index) in filteredRecords"
            :key="record.id"
            class="record-item"
            :class="{ active: currentRecord && currentRecord.id === record.id }"
            @click="selectRecord(record)"
          >
            <span class="dealer-code">{{ record.shortDealerCode || record.dealerCode || ('店铺' + (index + 1)) }}</span>
            <span class="status-badge" :class="getRecordStatusClass(record)">
              {{ getRecordStatusText(record) }}
            </span>
            <span class="item-count">{{ record.itemCount || 0 }}项</span>
          </div>
        </div>
        
        <div class="filter-option">
          <el-select v-model="statusFilter" size="mini" placeholder="筛选状态" clearable style="width: 100%;" @change="filterRecords">
            <el-option label="全部" value="" />
            <el-option label="待审核" value="pending" />
            <el-option label="已完成" value="completed" />
          </el-select>
        </div>
        
        <div class="record-info" v-if="currentRecord">
          <div class="info-row">
            <span class="label">店名称：</span>
            <span class="value">{{ currentRecord.dealerName || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="label">提交时间：</span>
            <span class="value">{{ formatTime(currentRecord.submitTime) }}</span>
          </div>
        </div>
      </div>

      <!-- 中间：申诉详情与审核 -->
      <div class="center-panel">
        <div v-if="currentRecord && currentItems.length > 0" class="appeal-list">
          <div
            v-for="(item, qIndex) in currentItems"
            :key="item.id"
            :ref="'item-' + qIndex"
            class="appeal-card"
          >
            <div class="card-header">
              <span class="item-label">【题目{{ qIndex + 1 }}】<span v-html="item.itemLabel"></span></span>
              <el-tag :type="getItemStatusType(item.reviewStatus)" size="mini">
                {{ getItemStatusText(item.reviewStatus) }}
              </el-tag>
            </div>
            
            <div class="card-body">
              <!-- 选项显示 -->
              <div v-if="item.optionsJson" class="item-options">
                <div class="options-label">答题选项：</div>
                <div class="options-list">
                  <div 
                    v-for="(opt, optIdx) in parseOptions(item.optionsJson)" 
                    :key="optIdx"
                    class="option-item"
                    :class="{ selected: opt.selected }"
                  >
                    <span class="option-text" v-html="opt.label"></span>
                    <el-tag v-if="opt.selected" type="warning" size="mini">已选</el-tag>
                  </div>
                </div>
              </div>
              
              <!-- 申诉内容（高亮区域） -->
              <div class="appeal-section">
                <div class="section-title">
                  <i class="el-icon-document"></i> 申诉内容
                </div>
                <div class="appeal-remark">
                  {{ item.appealRemark || '无申诉说明' }}
                </div>
                
                <!-- 凭证材料 -->
                <div class="evidence-area" v-if="item.evidenceUrls">
                  <label>凭证材料：</label>
                  <div class="evidence-gallery" v-viewer="{movable: false}">
                    <template v-for="(file, idx) in parseEvidenceUrls(item.evidenceUrls)">
                      <div v-if="isImage(file)" :key="idx" class="evidence-thumb">
                        <img :src="file.url" />
                      </div>
                      <a v-else :key="'f-'+idx" :href="file.url" target="_blank" class="evidence-file">
                        <i class="el-icon-document"></i>
                        {{ file.name || '附件' }}
                      </a>
                    </template>
                  </div>
                </div>
              </div>
              
              <!-- 审核操作区 -->
              <div class="review-action-section" v-if="item.reviewStatus === 0">
                <div class="action-row">
                  <span class="action-label">审核结果：</span>
                  <el-radio-group v-model="item.pendingStatus" size="small">
                    <el-radio-button :label="1">维持原判</el-radio-button>
                    <el-radio-button :label="2">修改失分原因</el-radio-button>
                    <el-radio-button :label="3">还分处理</el-radio-button>
                    <el-radio-button :label="4">不涉及</el-radio-button>
                  </el-radio-group>
                </div>
                
                <!-- 修改失分原因：显示非答案选项供勾选 -->
                <div class="action-row option-select-row" v-if="item.pendingStatus === 2 && item.allOptions && item.allOptions.length > 0">
                  <span class="action-label">选择选项：</span>
                  <div class="option-checkboxes">
                    <el-checkbox-group v-model="item.selectedOptionsInput">
                      <template v-for="opt in item.allOptions">
                        <el-checkbox 
                          v-if="!opt.isAnswer" 
                          :key="opt.option_key || opt.value" 
                          :label="opt.value"
                          class="option-checkbox"
                        >
                          <span v-html="opt.label"></span>
                        </el-checkbox>
                      </template>
                    </el-checkbox-group>
                    <div class="option-hint" v-if="getSelectableOptions(item, 2).length === 0">
                      <div class="hint-box info"><i class="el-icon-info"></i> 无可选择的非答案选项</div>
                    </div>
                  </div>
                </div>
                
                <!-- 还分处理：显示答案选项或有分值的选项供勾选 -->
                <div class="action-row option-select-row" v-if="item.pendingStatus === 3 && item.allOptions && item.allOptions.length > 0">
                  <span class="action-label">还分选项：</span>
                  <div class="option-checkboxes">
                    <el-checkbox-group v-model="item.selectedOptionsInput" @change="calcRestoredScore(item)">
                      <template v-for="opt in item.allOptions">
                        <el-checkbox 
                          v-if="opt.isAnswer || opt.hasScore" 
                          :key="opt.option_key || opt.value" 
                          :label="opt.value"
                          class="option-checkbox scored"
                        >
                          <span v-html="opt.label"></span>
                          <el-tag v-if="opt.score > 0" type="success" size="mini">+{{ opt.score }}分</el-tag>
                          <el-tag v-if="opt.isAnswer" type="warning" size="mini">答案</el-tag>
                        </el-checkbox>
                      </template>
                    </el-checkbox-group>
                    <div class="option-hint" v-if="getSelectableOptions(item, 3).length === 0">
                      <div class="hint-box info"><i class="el-icon-info"></i> 无可选择的答案/计分选项</div>
                    </div>
                  </div>
                </div>
                
                <!-- 还分处理时显示计算出的还分 -->
                <div class="action-row" v-if="item.pendingStatus === 3">
                  <span class="action-label">还分：</span>
                  <el-input-number 
                    v-model="item.restoredScoreInput" 
                    :min="0" 
                    :max="item.originalScore || 10"
                    size="small"
                    controls-position="right"
                  ></el-input-number>
                  <span class="score-hint">分 (根据选项自动计算，可手动调整)</span>
                </div>
                
                <div class="action-row">
                  <span class="action-label">审核备注：</span>
                  <el-input 
                    v-model="item.reviewRemarkInput" 
                    placeholder="输入审核意见（可选）"
                    size="small"
                    style="flex: 1;"
                  ></el-input>
                </div>
                
                <div class="action-row submit-row">
                  <el-button 
                    type="primary" 
                    size="small"
                    :disabled="!item.pendingStatus"
                    @click="submitItemReview(item)"
                  >
                    提交本题审核
                  </el-button>
                </div>
              </div>
              
              <!-- 已审核结果 -->
              <div class="reviewed-result" v-else>
                <div class="hint-box" :class="getReviewStatusStyle(item.reviewStatus)">
                  <i :class="item.reviewStatus === 3 ? 'el-icon-success' : 'el-icon-info'"></i>
                  <span>{{ getItemStatusText(item.reviewStatus) }}</span>
                  <span v-if="item.restoredScore" class="restored-score">（还分 +{{ item.restoredScore }}）</span>
                  <span v-if="item.reviewRemark" class="remark">- {{ item.reviewRemark }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-tip">
          <p v-if="!currentRecord">请从左侧选择一个店铺进行审核</p>
          <p v-else>该店铺暂无申诉记录</p>
        </div>
      </div>

      <!-- 右侧：概览与导航 -->
      <div class="right-panel">
        <div class="score-summary">
          <span class="label">审核后总分</span>
          <span class="score">{{ reviewedTotalScore }}</span>
        </div>
        
        <!-- 题目导航 -->
        <div class="nav-title">题目导航</div>
        <div class="question-nav">
          <div
            v-for="(item, i) in currentItems"
            :key="item.id"
            class="nav-item"
            :class="getNavItemClass(item)"
            @click="scrollToItem(i)"
          >
            {{ i + 1 }}
          </div>
        </div>
        
        <!-- 切换按钮 -->
        <div class="nav-buttons">
          <el-button size="small" :disabled="currentIndex <= 0" @click="prevRecord">上一份</el-button>
          <el-button size="small" :disabled="currentIndex >= filteredRecords.length - 1" @click="nextRecord">下一份</el-button>
        </div>
      </div>
    </div>
    
    <!-- 底部固定按钮栏 -->
    <div class="review-footer">
      <el-button 
        type="success" 
        size="medium" 
        @click="completeCurrentReview"
        :disabled="!canCompleteReview"
      >
        <i class="el-icon-check"></i> 完成当前门店审核
      </el-button>

    </div>
  </div>
</template>

<script>
import axios from 'index/axios'

export default {
  name: 'AppealReview',
  props: {
    batchId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      batchName: '',
      records: [],
      currentRecord: null,
      currentItems: [],
      currentIndex: -1,
      searchKeyword: '',
      statusFilter: '',
      answerTotalScore: 0  // 答卷原始总分
    }
  },
  computed: {
    filteredRecords() {
      let list = this.records
      
      if (this.searchKeyword) {
        const kw = this.searchKeyword.toLowerCase()
        list = list.filter(r => 
          (r.dealerCode || '').toLowerCase().includes(kw) ||
          (r.dealerName || '').toLowerCase().includes(kw)
        )
      }
      
      if (this.statusFilter === 'pending') {
        list = list.filter(r => r.status === 1) // 已提交待审核
      } else if (this.statusFilter === 'completed') {
        list = list.filter(r => r.status === 3) // 已完成
      }
      
      return list
    },
    totalCount() {
      return this.records.length
    },
    pendingCount() {
      return this.records.filter(r => r.status === 1).length
    },
    completedCount() {
      return this.records.filter(r => r.status === 3).length
    },
    originalTotalScore() {
      // 使用从 API 获取的答卷总分
      return this.answerTotalScore
    },
    reviewedTotalScore() {
      // 答卷总分 + 还分（只有状态3"还分处理"才加分）
      let restored = 0
      this.currentItems.forEach(item => {
        if (item.reviewStatus === 3 && item.restoredScore) {
          restored += item.restoredScore
        }
      })
      return this.answerTotalScore + restored
    },
    scoreChanged() {
      return this.reviewedTotalScore !== this.answerTotalScore
    },
    canCompleteReview() {
      if (!this.currentItems.length) return false
      return this.currentItems.every(item => item.reviewStatus !== 0)
    }
  },
  created() {
    this.loadBatchInfo()
    this.loadRecords()
  },
  methods: {
    async loadBatchInfo() {
      try {
        // 使用 batch/list API 获取批次信息
        const res = await axios.post('/Appeal/batch/list', {
          page: 1,
          pageSize: 100
        })
        const data = res?.data?.data || res?.data || {}
        const batches = data.records || []
        
        // 查找当前批次
        const currentBatch = batches.find(b => String(b.id) === String(this.batchId))
        if (currentBatch) {
          this.batchName = currentBatch.batchName || currentBatch.batchNo || '未命名批次'
        } else {
          this.batchName = '批次 #' + this.batchId
        }
      } catch (e) {
        console.error('加载批次信息失败', e)
        this.batchName = '批次 #' + this.batchId
      }
    },
    
    async loadRecords() {
      this.loading = true
      try {
        const res = await axios.post('/Appeal/record/list', {
          batchId: this.batchId,
          status: 1, // 只获取已提交的
          page: 1,
          pageSize: 1000
        })
        const data = res?.data?.data || res?.data || {}
        this.records = data.records || []
        
        if (this.records.length > 0) {
          // 尝试恢复上次审核位置
          const savedRecordId = this.getLastReviewPosition()
          const savedRecord = savedRecordId ? this.records.find(r => r.id == savedRecordId) : null
          
          if (savedRecord) {
            console.log('[AppealReview] Restoring last position:', savedRecordId)
            this.selectRecord(savedRecord)
          } else {
            this.selectRecord(this.records[0])
          }
        }
      } catch (e) {
        console.error('加载申诉记录失败', e)
      } finally {
        this.loading = false
      }
    },
    
    // 获取上次审核位置
    getLastReviewPosition() {
      try {
        const key = `appeal_review_position_${this.batchId}`
        return localStorage.getItem(key)
      } catch {
        return null
      }
    },
    
    // 保存当前审核位置
    saveReviewPosition(recordId) {
      try {
        const key = `appeal_review_position_${this.batchId}`
        localStorage.setItem(key, recordId)
      } catch (e) {
        console.warn('Failed to save review position', e)
      }
    },
    
    filterRecords() {
      // 触发计算属性刷新
    },
    
    async selectRecord(record) {
      this.currentRecord = record
      this.currentIndex = this.filteredRecords.findIndex(r => r.id === record.id)
      
      // 保存当前位置
      this.saveReviewPosition(record.id)
      
      try {
        const res = await axios.get(`/Appeal/record/detail/${record.id}`)
        const data = res?.data?.data || res?.data || {}
        
        // 保存答卷总分
        this.answerTotalScore = data.answerTotalScore || 0
        
        this.currentItems = (data.items || []).map(item => ({
          ...item,
          reviewRemarkInput: item.reviewRemark || '',
          restoredScoreInput: item.restoredScore || 0,
          pendingStatus: null,
          selectedOptionsInput: [] // 初始化选中选项数组
        }))
      } catch (e) {
        console.error('加载详情失败', e)
        this.currentItems = []
      }
    },
    
    prevRecord() {
      if (this.currentIndex > 0) {
        this.selectRecord(this.filteredRecords[this.currentIndex - 1])
      }
    },
    
    nextRecord() {
      if (this.currentIndex < this.filteredRecords.length - 1) {
        this.selectRecord(this.filteredRecords[this.currentIndex + 1])
      }
    },
    
    setItemStatus(item, status) {
      this.$set(item, 'pendingStatus', status)
    },
    
    // 获取可选择的选项（根据审核状态过滤）
    getSelectableOptions(item, status) {
      if (!item.allOptions || !item.allOptions.length) return []
      if (status === 2) {
        // 修改失分原因：只能选非答案选项
        return item.allOptions.filter(opt => !opt.isAnswer)
      } else if (status === 3) {
        // 还分处理：只能选答案选项或有分值的选项
        return item.allOptions.filter(opt => opt.isAnswer || opt.hasScore)
      }
      return item.allOptions
    },
    
    // 根据选中的选项计算还分
    calcRestoredScore(item) {
      const selectedValues = item.selectedOptionsInput || []
      const options = item.allOptions || []
      let totalScore = 0
      
      for (const opt of options) {
        if (selectedValues.includes(opt.value)) {
          totalScore += opt.score || 0
        }
      }
      
      this.$set(item, 'restoredScoreInput', totalScore)
    },
    
    async submitItemReview(item) {
      if (!item.pendingStatus) {
        this.$message.warning('请先选择审核结果')
        return
      }
      
      // 验证还分处理时需要选择选项
      if (item.pendingStatus === 3 && (!item.selectedOptionsInput || item.selectedOptionsInput.length === 0)) {
        this.$message.warning('请选择还分选项')
        return
      }
      
      try {
        await axios.post('/Appeal/item/review', {
          itemId: item.id,
          reviewStatus: item.pendingStatus,
          reviewRemark: item.reviewRemarkInput,
          restoredScore: item.pendingStatus === 3 ? item.restoredScoreInput : 0,
          // 传递选中的选项（状态2或3时）
          selectedOptions: (item.pendingStatus === 2 || item.pendingStatus === 3) 
            ? JSON.stringify(item.selectedOptionsInput) 
            : null
        })
        
        // 更新本地状态
        this.$set(item, 'reviewStatus', item.pendingStatus)
        this.$set(item, 'reviewRemark', item.reviewRemarkInput)
        this.$set(item, 'restoredScore', item.pendingStatus === 3 ? item.restoredScoreInput : 0)
        
        const statusMap = { 1: '维持原判', 2: '修改失分原因', 3: '还分处理', 4: '不涉及' }
        this.$message.success(`已设为: ${statusMap[item.pendingStatus]}`)
      } catch (e) {
        console.error('提交审核失败', e)
        this.$message.error('提交失败')
      }
    },
    
    async completeCurrentReview() {
      if (!this.canCompleteReview) {
        this.$message.warning('还有未审核的题目')
        return
      }
      
      // 更新左侧列表记录的状态为已完成
      if (this.currentRecord) {
        this.$set(this.currentRecord, 'status', 3) // 3 = 已完成
      }
      
      this.$message.success('当前门店审核完成')
      
      // 自动跳转下一份
      if (this.currentIndex < this.filteredRecords.length - 1) {
        this.nextRecord()
      }
    },
    
    scrollToItem(index) {
      console.log('[AppealReview] scrollToItem called, index:', index)
      const refKey = 'item-' + index
      const ref = this.$refs[refKey]
      console.log('[AppealReview] ref key:', refKey, 'ref:', ref)
      if (ref && ref[0]) {
        ref[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else if (ref) {
        // 如果 ref 不是数组
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    
    goBack() {
      this.$emit('back')
    },
    
    // 辅助方法
    formatTime(timeStr) {
      if (!timeStr) return '-'
      return new Date(timeStr).toLocaleString('zh-CN')
    },
    
    getRecordStatusClass(record) {
      if (record.status === 3) return 'completed'
      if (record.status === 1) return 'pending'
      return 'draft'
    },
    
    getRecordStatusText(record) {
      if (record.status === 3) return '已完成'
      if (record.status === 1) return '待审核'
      return '草稿'
    },
    
    getItemStatusType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      return 'info'
    },
    
    getItemStatusText(status) {
      const statusMap = {
        0: '待审核',
        1: '维持原判',
        2: '修改失分原因',
        3: '还分处理',
        4: '不涉及'
      }
      return statusMap[status] || '待审核'
    },
    
    getNavItemClass(item) {
      if (item.reviewStatus === 3) return 'restored' // 还分
      if (item.reviewStatus === 4) return 'not-applicable' // 不涉及
      if (item.reviewStatus >= 1) return 'reviewed' // 已处理
      return 'pending'
    },
    
    // 获取审核状态对应的样式类
    getReviewStatusStyle(status) {
      const styleMap = {
        1: 'info',      // 维持原判
        2: 'warning',   // 修改失分原因
        3: 'success',   // 还分处理
        4: 'info'       // 不涉及
      }
      return styleMap[status] || 'info'
    },
    
    // 获取短编码（取最后一段或截取后8位）
    getShortCode(dealerCode) {
      if (!dealerCode) return ''
      const parts = dealerCode.split(/[-_]/)
      if (parts.length > 1) {
        return parts[parts.length - 1]
      }
      if (dealerCode.length > 12) {
        return '...' + dealerCode.slice(-8)
      }
      return dealerCode
    },
    
    // 解析选项 JSON（与店端 appealNew.vue 一致）
    parseOptions(optionsJson) {
      if (!optionsJson) return []
      try {
        const parsed = JSON.parse(optionsJson)
        // 如果是数组，直接返回；如果是对象/值，包装成数组
        if (Array.isArray(parsed)) {
          return parsed.map(opt => ({
            label: opt.label || opt.text || opt.value || (typeof opt === 'string' ? opt : String(opt)),
            value: opt.value || opt,
            selected: true
          }))
        }
        // 单个值的情况
        return [{ label: String(parsed), value: parsed, selected: true }]
      } catch {
        // 非 JSON 格式，可能是逗号分隔的值
        if (optionsJson.includes(',')) {
          return optionsJson.split(',').filter(v => v.trim()).map(v => ({
            label: v.trim(),
            value: v.trim(),
            selected: true
          }))
        }
        return [{ label: optionsJson, value: optionsJson, selected: true }]
      }
    },
    
    parseEvidenceUrls(urlsStr) {
      if (!urlsStr) return []
      try {
        const parsed = JSON.parse(urlsStr)
        if (Array.isArray(parsed)) {
          return parsed.map(item => {
            if (typeof item === 'object' && item.url) return item
            return { url: item, name: '' }
          })
        }
        return []
      } catch {
        return urlsStr.split(',').filter(u => u).map(url => ({ url, name: '' }))
      }
    },
    
    isImage(file) {
      const url = typeof file === 'object' ? file.url : file
      if (!url) return false
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url) || url.includes('/image/')
    }
  }
}
</script>

<style lang="scss" scoped>
.appeal-review-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.review-header {
  height: 54px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }
  
  .header-center {
    .stats {
      color: #606266;
      font-size: 14px;
      
      strong {
        color: #409eff;
        margin: 0 2px;
      }
    }
  }
}

.review-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.left-panel {
  width: 240px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  
  .panel-header {
    padding: 16px;
    border-bottom: 1px solid #e4e7ed;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .panel-title {
      font-weight: 600;
      color: #303133;
    }
    
    .progress {
      font-size: 12px;
      color: #909399;
    }
  }
  
  .search-box {
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .record-list {
    flex: 1;
    overflow-y: auto;
    
    .record-item {
      padding: 12px 16px;
      border-bottom: 1px solid #f5f5f5;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: background 0.2s;
      
      &:hover {
        background: #f5f7fa;
      }
      
      &.active {
        background: #ecf5ff;
        border-left: 3px solid #409eff;
      }
      
      .dealer-code {
        flex: 1;
        font-size: 13px;
        color: #303133;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .status-badge {
        font-size: 10px;
        padding: 2px 6px;
        border-radius: 10px;
        
        &.pending {
          background: #fef0f0;
          color: #f56c6c;
        }
        
        &.completed {
          background: #f0f9eb;
          color: #67c23a;
        }
      }
      
      .item-count {
        font-size: 11px;
        color: #909399;
      }
    }
  }
  
  .filter-option {
    padding: 12px;
    border-top: 1px solid #e4e7ed;
  }
  
  .record-info {
    padding: 12px 16px;
    border-top: 1px solid #e4e7ed;
    background: #fafafa;
    
    .info-row {
      display: flex;
      font-size: 12px;
      margin-bottom: 6px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .label {
        color: #909399;
        width: 70px;
      }
      
      .value {
        color: #606266;
        flex: 1;
      }
    }
  }
}

.center-panel {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  .appeal-list {
    max-width: 800px;
    margin: 0 auto;
  }
  
  .appeal-card {
    background: #fff;
    border-radius: 8px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    
    .card-header {
      padding: 14px 20px;
      background: #f5f7fa;
      display: flex;
      align-items: center;
      gap: 12px;
      
      .item-title {
        font-weight: 600;
        color: #303133;
      }
      
      .item-label {
        flex: 1;
        color: #606266;
        font-size: 14px;
      }
    }
    
    .card-body {
      padding: 20px;
      
      .item-options {
        margin-bottom: 16px;
        
        .options-label {
          color: #409eff;
          font-weight: 500;
          margin-bottom: 10px;
        }
        
        .options-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          
          .option-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 14px;
            background: #f5f7fa;
            border-radius: 4px;
            border: 1px solid #e4e7ed;
            
            &.selected {
              background: #fdf6ec;
              border-color: #e6a23c;
            }
            
            .option-text {
              flex: 1;
              color: #606266;
              font-size: 14px;
            }
          }
        }
      }
      
      .appeal-section {
        background: #fffbe6;
        border: 1px solid #ffe58f;
        border-radius: 6px;
        padding: 16px;
        margin-bottom: 16px;
        
        .section-title {
          color: #d48806;
          font-weight: 500;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .appeal-remark {
          color: #595959;
          line-height: 1.6;
          margin-bottom: 12px;
        }
        
        .evidence-area {
          label {
            display: block;
            color: #8c8c8c;
            font-size: 12px;
            margin-bottom: 8px;
          }
          
          .evidence-gallery {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            
            .evidence-thumb {
              img {
                width: 60px;
                height: 60px;
                object-fit: cover;
                border-radius: 4px;
                cursor: pointer;
                border: 1px solid #e4e7ed;
                
                &:hover {
                  border-color: #409eff;
                }
              }
            }
            
            .evidence-file {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 4px 8px;
              background: #fff;
              border: 1px solid #e4e7ed;
              border-radius: 4px;
              color: #409eff;
              font-size: 12px;
              text-decoration: none;
              
              &:hover {
                background: #ecf5ff;
              }
            }
          }
        }
      }
      
      .review-action-section {
        padding-top: 16px;
        border-top: 1px dashed #e4e7ed;
        
        .action-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
          
          .action-label {
            width: 80px;
            color: #606266;
            font-size: 13px;
            flex-shrink: 0;
          }
          
          .score-hint {
            color: #909399;
            font-size: 13px;
          }
          
          &.option-select-row {
            align-items: flex-start;
            
            .option-checkboxes {
              flex: 1;
              
              .el-checkbox-group {
                display: flex;
                flex-direction: column;
                gap: 8px;
              }
              
              .option-checkbox {
                display: flex;
                align-items: center;
                padding: 10px 14px;
                background: #f5f7fa;
                border-radius: 4px;
                border: 1px solid #e4e7ed;
                margin: 0;
                
                &.is-checked {
                  background: #ecf5ff;
                  border-color: #409eff;
                }
                
                &.scored.is-checked {
                  background: #f0f9eb;
                  border-color: #67c23a;
                }
                
                .el-checkbox__label {
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  flex-wrap: wrap;
                }
              }
              
              .option-hint {
                margin-top: 8px;
              }
            }
          }
          
          &.submit-row {
            margin-top: 16px;
            justify-content: flex-end;
          }
        }
      }
      
      .reviewed-result {
        margin-top: 16px;
        
        .hint-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 4px;
          font-size: 14px;
          
          i {
            font-size: 16px;
          }
          
          &.info {
            background: #f4f4f5;
            color: #909399;
            border: 1px solid #e4e7ed;
          }
          
          &.success {
            background: #f0f9eb;
            color: #67c23a;
            border: 1px solid #c2e7b0;
          }
          
          &.warning {
            background: #fdf6ec;
            color: #e6a23c;
            border: 1px solid #f5dab1;
          }
          
          .restored-score {
            color: #67c23a;
            font-weight: 500;
          }
          
          .remark {
            color: #909399;
            margin-left: 8px;
          }
        }
      }
    }
  }
  
  .empty-tip {
    text-align: center;
    padding: 100px 0;
    color: #909399;
  }
}

.right-panel {
  width: 180px;
  background: #fff;
  border-left: 1px solid #e4e7ed;
  padding: 20px;
  display: flex;
  flex-direction: column;
  
  .score-summary {
    text-align: center;
    margin-bottom: 16px;
    
    .label {
      display: block;
      color: #909399;
      font-size: 12px;
      margin-bottom: 4px;
    }
    
    .score {
      font-size: 32px;
      font-weight: 600;
      color: #303133;
      
      &.changed {
        color: #67c23a;
      }
    }
    
    &.review-score .score {
      color: #409eff;
    }
  }
  
  .nav-title {
    color: #909399;
    font-size: 12px;
    margin-bottom: 12px;
  }
  
  .question-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 20px;
    
    .nav-item {
      width: 26px;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      font-size: 11px;
      cursor: pointer;
      transition: all 0.2s;
      
      &:hover {
        transform: scale(1.1);
      }
      
      &.pending {
        background: #f5f7fa;
        color: #909399;
      }
      
      &.reviewed {
        background: #409eff;
        color: #fff;
      }
      
      &.restored {
        background: #67c23a;
        color: #fff;
      }
      
      &.not-applicable {
        background: #909399;
        color: #fff;
      }
    }
  }
  
  .nav-buttons {
    margin-top: auto;
    display: flex;
    flex-direction: row;
    gap: 8px;
    
    .el-button {
      flex: 1;
    }
  }
}

.review-footer {
  height: 56px;
  background: #fff;
  border-top: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

// 响应式布局 - 平板（<= 1200px）
@media screen and (max-width: 1200px) {
  .right-panel {
    width: 140px;
    padding: 12px;
    
    .item-nav {
      .nav-item {
        width: 28px;
        height: 28px;
        font-size: 12px;
      }
    }
  }
  
  .left-panel {
    width: 220px;
  }
}

// 响应式布局 - 小屏（<= 992px）
@media screen and (max-width: 992px) {
  .right-panel {
    display: none; // 隐藏右侧导航
  }
  
  .left-panel {
    width: 180px;
    
    .store-item {
      padding: 10px;
      
      .store-name {
        font-size: 12px;
      }
    }
  }
  
  .center-panel {
    padding: 12px;
    
    .appeal-list {
      max-width: 100%;
    }
  }
}

// 响应式布局 - 移动端（<= 768px）
@media screen and (max-width: 768px) {
  .review-container {
    flex-direction: column;
    
    .left-panel {
      width: 100%;
      height: auto;
      max-height: 200px;
      border-right: none;
      border-bottom: 1px solid #e4e7ed;
      
      .store-list {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        padding: 8px;
        gap: 8px;
        
        .store-item {
          flex-shrink: 0;
          width: auto;
          min-width: 120px;
        }
      }
    }
    
    .center-panel {
      flex: 1;
      min-height: 0;
    }
  }
  
  .review-header {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 8px;
    
    .header-left h2 {
      font-size: 16px;
    }
    
    .header-stats {
      gap: 12px;
      font-size: 12px;
    }
  }
  
  .review-footer {
    height: auto;
    padding: 12px;
    flex-wrap: wrap;
  }
}
</style>
